import { usePutFrontRichiesteIdModuliId } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { ConfirmUpdateLogic } from '@framework/common-web';
import { serializeFormSectionsValues } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import { ObjectSchema } from '@framework/validation';
import { message } from 'antd';
import { useCallback } from 'react';
import { useSubmissionConfirmUpdate } from './useSubmissionConfirmUpdate';

/**
 * Hook che torna la funzione di "submit" per il salvataggio e
 * completamento di un modulo.
 */
export function useSubmitModuleForm(params: {
  moduleId: string | null | undefined;
  submissionId: string | null | undefined;
  validationSchema: ObjectSchema<any>;
  onSuccess?: () => Promise<void>;
  onBeforeSave?: () => Promise<void>;
  onAfterSave?: (isValid: boolean) => Promise<void>;
}) {
  const {
    validationSchema,
    moduleId,
    submissionId,
    onSuccess,
    onBeforeSave,
    onAfterSave,
  } = params;

  const confirmUpdate = useSubmissionConfirmUpdate();

  const save = usePutFrontRichiesteIdModuliId();
  const { t } = useTranslation();

  const forceSave = usePutFrontRichiesteIdModuliId({
    request: { headers: { forceSave: true } },
  });

  const saveForm = useCallback(
    async (params: {
      submissionId: string | null | undefined;
      moduleId: string | null | undefined;
      values: any;
      force: boolean;
    }) => {
      const { submissionId, moduleId, values, force } = params;

      if (!moduleId) {
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { moduleId }); // prettier-ignore
        message.error(t("error-submit-module-form.errore-salvataggio-modulo", 'Errore durante il salvataggio, ID Modulo non presente')); // prettier-ignore
        return;
      }

      if (!submissionId) {
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionId }); // prettier-ignore
        message.error(t("error-submit-module-form.errore-salvataggio-richiesta", 'Errore durante il salvataggio, ID Richiesta non presente')); // prettier-ignore
        return;
      }

      const saveFn = force ? forceSave : save;

      await onBeforeSave?.();
      await saveFn.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: moduleId,
        data: {
          statoValidazione: {
            valido: true,
            visitato: true,
            completato: true,
          },
          valoriSezioni: serializeFormSectionsValues(validationSchema, values),
        },
      });

      await onAfterSave?.(true);

      await onSuccess?.();
    },
    [
      forceSave,
      onAfterSave,
      onBeforeSave,
      onSuccess,
      save,
      t,
      validationSchema,
    ],
  );

  const onSubmit = useCallback(
    async (values: any) => {
      try {
        await saveForm({ submissionId, moduleId, values, force: false });
      } catch (e: any) {
        if (ConfirmUpdateLogic.needUserConfirm(e)) {
          confirmUpdate({
            differences: e.response.data,
            onOk: async () => {
              await saveForm({ submissionId, moduleId, values, force: true });
            },
          });
          return;
        }

        console.error(`[SubmitModuleForm] Errore durante il salvataggio dei dati`, { error: e }); // prettier-ignore
        message.error(getNetworkErrorMessage(e, t("error-submit-module-form.error-saving",'Errore durante il salvataggio'))); // prettier-ignore
        /**
         * Rilanciamo l'eccezione. In questo modo possiamo gestire il fallimento della chiamata nei componenti
         * che richiamano il submit programmaticamente (come `ManagedSubmitButton`). Se non rilanciassimo l'eccezione,
         * in quei componenti la submit verrebbe considerata sempre valida.
         * N.B. Questo significa che questa on submit _non può essere chiamata_ con un onSubmitButton, che in caso di errore
         * porterebbe a un'eccezione non gestita.
         */
        throw e;
      }
    },
    [confirmUpdate, moduleId, saveForm, submissionId, t],
  );

  return onSubmit;
}
